import { inject, Injectable } from '@angular/core';
import { Room } from 'src/app/model/room/room';
import { RoomModel } from 'src/app/model/room/room.model';
import { StorageService } from '../storage/storage.service';
import { OperatorService } from '../operator/operator.service';

interface StoredRoomData {
  model: RoomModel;
  id: string;
  path: string;
}

@Injectable({
  providedIn: 'root',
})
export class DemoRoomService {
  private storageService = inject(StorageService);
  private operatorService = inject(OperatorService);
  private rooms: Room[] | undefined = undefined;
  private readonly ROOMS_KEY = `${this.operatorService.gameId}_demo_rooms`;

  async loadRooms(): Promise<void> {
    if (this.rooms) {
      return;
    }

    try {
      const storedRooms = await this.storageService.getItem<StoredRoomData[]>(this.ROOMS_KEY);
      if (storedRooms) {
        this.rooms = storedRooms.map((roomData) => this.roomFromData(roomData));
      }
    } catch (error) {
      console.error('Error loading rooms from storage', error);
    }
  }

  async addRoom(room: Room): Promise<void> {
    if (!this.rooms) {
      this.rooms = [];
    }
    this.rooms.push(room);
    await this.saveRooms();
  }

  getRoomById(id: string): Room | undefined {
    if (!this.rooms) {
      return;
    }
    return this.rooms.find((room) => room.id === id);
  }

  getRoomIds(): string[] {
    if (!this.rooms) {
      return [];
    }
    return this.rooms.map((room) => room.id).reverse();
  }

  clear() {
    this.rooms = undefined;
    return this.storageService.removeItem(this.ROOMS_KEY);
  }

  private async saveRooms(): Promise<void> {
    try {
      await this.storageService.setItem(this.ROOMS_KEY, this.rooms);
    } catch (error) {
      console.error('Error saving rooms to storage', error);
    }
  }

  private roomFromData(roomData: StoredRoomData): Room {
    return new Room(roomData.model, roomData.id, roomData.path);
  }
}
